import { createAction, handleActions } from 'redux-actions';
import { Record, Map, List } from 'immutable';
import { pender } from 'redux-pender';

import { camelizeObjKeys } from '~/utils/CamelizeUtils.js';
import { format, tel } from '~/utils/FormatUtils.js';
import constants from '~/constants.js';
import * as api from '~/lib/api';

const CLEAR = 'agentDetail/CLEAR';
const GET_AGENT = 'agentDetail/GET_AGENT';
const CALL_AGENT = 'agentDetail/CALL_AGENT';
const SHOW_MODAL = 'agentDetail/SHOW_MODAL';
const CLOSE_MODAL = 'agentDetail/CLOSE_MODAL';
const INIT_REVIEW_LIST = 'agentDetail/INIT_REVIEW_LIST';
const SET_REVIEW_LIST = 'agentDetail/SET_REVIEW_LIST';
const GET_NEXT_REVIEWS = 'agentDetail/GET_NEXT_REVIEWS';

export const clear = createAction(CLEAR);
export const getAgent = createAction(GET_AGENT, agentId =>
  api.getAgent(agentId)
);
export const callAgent = createAction(CALL_AGENT, agentId =>
  api.callAgent(agentId)
);
export const showModal = createAction(SHOW_MODAL, modalId => modalId);
export const closeModal = createAction(CLOSE_MODAL);
export const initReviewList = createAction(INIT_REVIEW_LIST, id =>
  api.getReviewList({
    id,
    page: 0,
  })
);
export const setReviewList = createAction(
  SET_REVIEW_LIST,
  reviewList => reviewList
);
export const getNextReviewList = createAction(
  GET_NEXT_REVIEWS,
  ({ id, page }) =>
    api.getReviewList({
      id,
      page,
    })
);

const Ratings = Record({
  overall: null,
  expertise: null,
  responsiveness: null,
  reliability: null,
  price: null,
});

const Review = Record({
  id: 0,
  classification: '',
  overall: 0,
  createdAt: null,
  text: '',
});

const initialState = Record({
  id: 0,
  name: '',
  address: '',
  phoneNumber: '',
  clients: '',
  views: '',
  calls: '',
  reviews: '',
  ratings: Ratings(),
  classifications: List(),
  favored: false,
  modalIdShown: 0,
  originalReviewList: List(),
  reviewList: List(),
  isMoreReviews: false,
  reviewsCurrentPage: 0,
  reviewsTotalPages: 0,
})();

export default handleActions(
  {
    [CLEAR]: state => initialState,
    [CALL_AGENT]: state => state,
    [SHOW_MODAL]: (state, { payload: modalId }) => {
      return state.set('modalIdShown', modalId);
    },
    [CLOSE_MODAL]: state => state.set('modalIdShown', 0),
    [SET_REVIEW_LIST]: (state, { payload: reviewList }) =>
      state.merge(
        Map({
          reviewList,
          isMoreReviews: state.reviewsCurrentPage < state.reviewsTotalPages,
        })
      ),
    ...pender({
      type: GET_AGENT,
      onSuccess: (state, { payload: response }) => {
        const data = camelizeObjKeys(response.data);
        return state.merge(
          Map({
            ...data,
            phoneNumber: data.phoneNumber ? tel(data.phoneNumber) : '',
            clients: format(data.clients),
            views: format(data.views, 'kr'),
            calls: format(data.calls, 'kr'),
            classifications: List(
              data.classifications.map(cl => {
                const name = cl.name;
                const count = format(cl.count);
                return `${name}(${count})`;
              })
            ),
            reviews: format(data.reviews, 'kr'),
            ratings: Ratings(data.ratings),
            favored: data.clients >= constants.numClientsForFavoredLabel,
          })
        );
      },
    }),
    ...pender({
      type: INIT_REVIEW_LIST,
      onSuccess: (state, { payload: response }) => {
        const data = camelizeObjKeys(response.data);
        return state.merge(
          Map({
            originalReviewList: List(
              data.reviews.map(review => Review(review))
            ),
            reviewList: List(
              data.reviews.slice(0, 3).map(review => Review(review))
            ),
            isMoreReviews: data.reviews.length > 3,
            reviewsCurrentPage: 1,
            reviewsTotalPages: data.totalPages,
          })
        );
      },
    }),
    ...pender({
      type: GET_NEXT_REVIEWS,
      onSuccess: (state, { payload: response }) => {
        const data = camelizeObjKeys(response.data);
        return state.merge(
          Map({
            reviewList: state.reviewList.concat(
              List(data.reviews.map(review => Review(review)))
            ),
            isMoreReviews: data.currentPage < data.totalPages,
            reviewsCurrentPage: data.currentPage,
            reviewsTotalPages: data.totalPages,
          })
        );
      },
    }),
  },
  initialState
);



// WEBPACK FOOTER //
// ./src/store/modules/agentDetail.js